/**
 * PriorityMatrix — Bain-style 2×2 scatter plot.
 *
 * X-axis: Relatedness to Core (composite Bain score)
 * Y-axis: Attractiveness (composite score)
 * Bubbles sized by SAM (sqrt scaling), colored by initiative.
 * Optional related-link overlay shows cross-axis strategic dependencies.
 */

import { useState, useMemo, useCallback, useRef } from 'react';
import { useAdjacencyStore } from '../../store/adjacency-store';
import type { AdjacencyNode } from '../../types/adjacency';
import { INITIATIVE_COLORS } from '../../utils/map-colors';
import { nodePassesFilter } from '../../utils/filter-nodes';
import { relatednessScore, formatRelatedness } from '../../utils/relatedness';
import { NodeModal } from '../editors/NodeModal';

// ── Constants ────────────────────────────────────────────────────────

const PADDING = { top: 40, right: 40, bottom: 50, left: 55 };
const GRID_MID = 5.5;     // Quadrant divider on both axes
const SCORE_MIN = 0.5;
const SCORE_MAX = 10.5;

const QUADRANTS = [
  { label: 'Invest & Grow', x: 'right', y: 'top', color: 'rgba(34,197,94,0.08)', textColor: '#4ade80' },
  { label: 'Evaluate Carefully', x: 'left', y: 'top', color: 'rgba(245,158,11,0.08)', textColor: '#fbbf24' },
  { label: 'Optimize & Harvest', x: 'right', y: 'bottom', color: 'rgba(59,130,246,0.08)', textColor: '#60a5fa' },
  { label: 'Deprioritize', x: 'left', y: 'bottom', color: 'rgba(107,114,128,0.08)', textColor: '#9ca3af' },
] as const;

// ── Helpers ──────────────────────────────────────────────────────────

// WQ-343 D1(a): the weighted formula lives in utils/relatedness, shared with the node editor
const relatednessComposite = relatednessScore;

const DEFAULT_BUBBLE_RADIUS = 8;
const MIN_BUBBLE_RADIUS = 6;
const MAX_BUBBLE_RADIUS = 28;

function bubbleRadius(sam: number | null, samMax: number): number {
  if (sam == null || sam <= 0 || samMax <= 0) return DEFAULT_BUBBLE_RADIUS;
  // Scale: sqrt(sam / samMax) so area is proportional to value
  const ratio = Math.sqrt(sam / samMax);
  return MIN_BUBBLE_RADIUS + ratio * (MAX_BUBBLE_RADIUS - MIN_BUBBLE_RADIUS);
}

/** Simple jitter: offset overlapping nodes slightly. */
function jitterPositions(
  items: { node: AdjacencyNode; rx: number; ry: number }[],
  samMax: number,
): { node: AdjacencyNode; rx: number; ry: number; jx: number; jy: number }[] {
  const result: { node: AdjacencyNode; rx: number; ry: number; jx: number; jy: number }[] = [];
  const placed: { x: number; y: number; r: number }[] = [];

  for (const item of items) {
    let jx = 0;
    let jy = 0;
    const r = bubbleRadius(item.node.sam, samMax);

    // Check collision with already-placed bubbles
    for (const p of placed) {
      const dx = item.rx - p.x;
      const dy = item.ry - p.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const minDist = r + p.r + 2;
      if (dist < minDist) {
        // Push away
        const angle = Math.atan2(dy, dx) || (Math.random() * Math.PI * 2);
        const push = minDist - dist;
        jx += Math.cos(angle) * push;
        jy += Math.sin(angle) * push;
      }
    }

    placed.push({ x: item.rx + jx, y: item.ry + jy, r });
    result.push({ ...item, jx, jy });
  }
  return result;
}

// ── Component ────────────────────────────────────────────────────────

export function PriorityMatrix() {
  const {
    nodes, filters, selectedNodeId, selectNode,
    updateNode, deleteNode, duplicateNode,
    createParentChildLink, createRelatedLink, removeRelationship, convertEdgeType,
  } = useAdjacencyStore();

  const [showLinks, setShowLinks] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [detailNodeId, setDetailNodeId] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });

  // Measure container
  const containerRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width: Math.max(width, 400), height: Math.max(height, 300) });
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const plotW = dimensions.width - PADDING.left - PADDING.right;
  const plotH = dimensions.height - PADDING.top - PADDING.bottom;

  const scaleX = useCallback((v: number) =>
    PADDING.left + ((v - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * plotW,
  [plotW]);

  const scaleY = useCallback((v: number) =>
    PADDING.top + plotH - ((v - SCORE_MIN) / (SCORE_MAX - SCORE_MIN)) * plotH,
  [plotH]);

  // Filter + position
  const filteredNodes = useMemo(
    () => nodes.filter((n) => nodePassesFilter(n, filters)),
    [nodes, filters],
  );

  const samMax = useMemo(
    () => Math.max(...filteredNodes.map((n) => n.sam ?? 0), 1),
    [filteredNodes],
  );

  const positioned = useMemo(() => {
    const raw = filteredNodes.map((node) => ({
      node,
      rx: scaleX(relatednessComposite(node)),
      ry: scaleY(node.attractivenessScore),
    }));
    return jitterPositions(raw, samMax);
  }, [filteredNodes, scaleX, scaleY, samMax]);

  // Related links
  const relatedLinks = useMemo(() => {
    if (!showLinks) return [];
    const posMap = new Map(positioned.map((p) => [p.node.id, p]));
    const links: { x1: number; y1: number; x2: number; y2: number; key: string }[] = [];
    const seen = new Set<string>();

    for (const p of positioned) {
      for (const edge of p.node.edges) {
        if (edge.relationship !== 'peer') continue;
        const target = posMap.get(edge.targetId);
        if (!target) continue;
        const key = [p.node.id, edge.targetId].sort().join('-');
        if (seen.has(key)) continue;
        seen.add(key);
        links.push({
          x1: p.rx + p.jx,
          y1: p.ry + p.jy,
          x2: target.rx + target.jx,
          y2: target.ry + target.jy,
          key,
        });
      }
    }
    return links;
  }, [positioned, showLinks]);

  const hoveredNode = hoveredId ? nodes.find((n) => n.id === hoveredId) : null;
  const detailNode = detailNodeId ? nodes.find((n) => n.id === detailNodeId) : null;

  // Tick values
  const ticks = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Toolbar */}
      <div className="flex items-center gap-3 mb-2">
        <label className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
          <input
            type="checkbox"
            checked={showLinks}
            onChange={(e) => setShowLinks(e.target.checked)}
            className="accent-blue-500"
          />
          Show Related Links
        </label>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
          {filteredNodes.length} nodes
        </span>
      </div>

      {/* SVG Chart */}
      <div ref={containerRef} className="glass-panel flex-1 min-h-0 relative overflow-hidden rounded-lg">
        <svg
          ref={svgRef}
          width={dimensions.width}
          height={dimensions.height}
          style={{ display: 'block' }}
        >
          {/* Quadrant backgrounds */}
          {QUADRANTS.map((q) => {
            const x = q.x === 'left' ? scaleX(SCORE_MIN) : scaleX(GRID_MID);
            const y = q.y === 'top' ? scaleY(SCORE_MAX) : scaleY(GRID_MID);
            const w = q.x === 'left' ? scaleX(GRID_MID) - scaleX(SCORE_MIN) : scaleX(SCORE_MAX) - scaleX(GRID_MID);
            const h = q.y === 'top' ? scaleY(GRID_MID) - scaleY(SCORE_MAX) : scaleY(SCORE_MIN) - scaleY(GRID_MID);
            const labelX = x + w / 2;
            const labelY = y + h / 2;
            return (
              <g key={q.label}>
                <rect x={x} y={y} width={w} height={h} fill={q.color} />
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={13}
                  fontWeight={600}
                  fill={q.textColor}
                  opacity={0.5}
                >
                  {q.label}
                </text>
              </g>
            );
          })}

          {/* Grid lines */}
          {ticks.map((t) => (
            <g key={`grid-${t}`}>
              <line
                x1={scaleX(t)} y1={scaleY(SCORE_MIN)} x2={scaleX(t)} y2={scaleY(SCORE_MAX)}
                stroke="rgba(255,255,255,0.06)" strokeWidth={1}
              />
              <line
                x1={scaleX(SCORE_MIN)} y1={scaleY(t)} x2={scaleX(SCORE_MAX)} y2={scaleY(t)}
                stroke="rgba(255,255,255,0.06)" strokeWidth={1}
              />
            </g>
          ))}

          {/* Quadrant divider lines */}
          <line
            x1={scaleX(GRID_MID)} y1={scaleY(SCORE_MIN)} x2={scaleX(GRID_MID)} y2={scaleY(SCORE_MAX)}
            stroke="rgba(255,255,255,0.2)" strokeWidth={1.5} strokeDasharray="6 3"
          />
          <line
            x1={scaleX(SCORE_MIN)} y1={scaleY(GRID_MID)} x2={scaleX(SCORE_MAX)} y2={scaleY(GRID_MID)}
            stroke="rgba(255,255,255,0.2)" strokeWidth={1.5} strokeDasharray="6 3"
          />

          {/* Axes */}
          <line
            x1={PADDING.left} y1={scaleY(SCORE_MIN)} x2={PADDING.left + plotW} y2={scaleY(SCORE_MIN)}
            stroke="rgba(255,255,255,0.3)" strokeWidth={1}
          />
          <line
            x1={PADDING.left} y1={scaleY(SCORE_MIN)} x2={PADDING.left} y2={scaleY(SCORE_MAX)}
            stroke="rgba(255,255,255,0.3)" strokeWidth={1}
          />

          {/* X-axis ticks + labels */}
          {ticks.map((t) => (
            <text
              key={`x-${t}`}
              x={scaleX(t)}
              y={scaleY(SCORE_MIN) + 18}
              textAnchor="middle"
              fontSize={10}
              fill="var(--text-dim)"
            >
              {t}
            </text>
          ))}
          <text
            x={PADDING.left + plotW / 2}
            y={dimensions.height - 6}
            textAnchor="middle"
            fontSize={11}
            fontWeight={600}
            fill="var(--text-muted)"
          >
            Relatedness to Core
          </text>

          {/* Y-axis ticks + labels */}
          {ticks.map((t) => (
            <text
              key={`y-${t}`}
              x={PADDING.left - 8}
              y={scaleY(t) + 3}
              textAnchor="end"
              fontSize={10}
              fill="var(--text-dim)"
            >
              {t}
            </text>
          ))}
          <text
            x={14}
            y={PADDING.top + plotH / 2}
            textAnchor="middle"
            fontSize={11}
            fontWeight={600}
            fill="var(--text-muted)"
            transform={`rotate(-90, 14, ${PADDING.top + plotH / 2})`}
          >
            Attractiveness
          </text>

          {/* Related link lines */}
          {relatedLinks.map((link) => (
            <line
              key={link.key}
              x1={link.x1} y1={link.y1} x2={link.x2} y2={link.y2}
              stroke="#f59e0b"
              strokeWidth={1}
              strokeDasharray="4 2"
              opacity={0.5}
            />
          ))}

          {/* Bubbles */}
          {positioned.map(({ node, rx, ry, jx, jy }) => {
            const cx = rx + jx;
            const cy = ry + jy;
            const r = bubbleRadius(node.sam, samMax);
            const color = INITIATIVE_COLORS[node.strategicInitiative];
            const isSelected = node.id === selectedNodeId;
            const isHovered = node.id === hoveredId;

            return (
              <g key={node.id}>
                <circle
                  cx={cx}
                  cy={cy}
                  r={r}
                  fill={color}
                  opacity={isSelected || isHovered ? 0.95 : 0.7}
                  stroke={isSelected ? '#fff' : isHovered ? '#93c5fd' : 'transparent'}
                  strokeWidth={isSelected ? 2 : 1.5}
                  className="cursor-pointer transition-opacity"
                  onClick={() => selectNode(node.id)}
                  onDoubleClick={() => { selectNode(node.id); setDetailNodeId(node.id); }}
                  onMouseEnter={() => setHoveredId(node.id)}
                  onMouseLeave={() => setHoveredId(null)}
                />
                {/* Label for large bubbles */}
                {r >= 12 && (
                  <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={8}
                    fill="#fff"
                    fontWeight={600}
                    pointerEvents="none"
                    opacity={0.9}
                  >
                    {node.name.length > 10 ? node.name.slice(0, 9) + '…' : node.name}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {hoveredNode && (
          <Tooltip node={hoveredNode} />
        )}
      </div>

      {/* Formula Reference */}
      <div className="glass-panel rounded-lg px-5 py-3 mt-2 shrink-0">
        <div className="text-[10px] font-semibold uppercase tracking-wider mb-2.5" style={{ color: 'var(--text-muted)' }}>
          Matrix Dimensions
        </div>
        <div className="grid grid-cols-3 gap-6">
          {/* X-axis */}
          <div>
            <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>
              X-Axis: Relatedness to Core
            </div>
            <code
              className="inline-block px-2 py-1 rounded text-[11px] mb-1.5"
              style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.2)', color: '#93c5fd' }}
            >
              SC×0.4 + SCo×0.3 + CC×0.3
            </code>
            <div className="text-[11px] leading-5" style={{ color: 'var(--text-secondary)' }}>
              SC = Shared Customers · SCo = Shared Costs · CC = Common Competitors — each scored 1–10
            </div>
          </div>

          {/* Y-axis */}
          <div>
            <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>
              Y-Axis: Attractiveness
            </div>
            <code
              className="inline-block px-2 py-1 rounded text-[11px] mb-1.5"
              style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)', color: '#fcd34d' }}
            >
              1–10 manual rating
            </code>
            <div className="text-[11px] leading-5" style={{ color: 'var(--text-secondary)' }}>
              Market size, growth, differentiation, profitability, and leadership opportunity
            </div>
          </div>

          {/* Bubble size */}
          <div>
            <div className="text-xs font-semibold mb-1.5" style={{ color: 'var(--text-primary)' }}>
              Bubble Size: SAM
            </div>
            <code
              className="inline-block px-2 py-1 rounded text-[11px] mb-1.5"
              style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)', color: '#86efac' }}
            >
              Area ∝ √(SAM / max SAM)
            </code>
            <div className="text-[11px] leading-5" style={{ color: 'var(--text-secondary)' }}>
              Serviceable Addressable Market ($). Default dot if SAM not set.
            </div>
          </div>
        </div>
      </div>

      {/* NodeModal */}
      {detailNode && (
        <NodeModal
          node={detailNode}
          allNodes={nodes}
          onClose={() => setDetailNodeId(null)}
          onSave={(id, changes) => { void updateNode(id, changes); }}
          onDelete={(id) => { void deleteNode(id); setDetailNodeId(null); }}
          onDuplicate={(id) => { void duplicateNode(id); }}
          onAddParent={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
          onAddChild={(parentId, childId) => { void createParentChildLink(parentId, childId); }}
          onAddRelated={(a, b) => { void createRelatedLink(a, b); }}
          onRemoveRelationship={(a, b) => { void removeRelationship(a, b); }}
          onConvertEdgeType={(a, b, toType, parentId) => { void convertEdgeType(a, b, toType, parentId); }}
        />
      )}
    </div>
  );
}

// ── Tooltip ──────────────────────────────────────────────────────────

function Tooltip({ node }: { node: AdjacencyNode }) {
  const rel = relatednessComposite(node);
  // Position tooltip near cursor — simplified to fixed position in top-right
  return (
    <div
      className="absolute top-3 right-3 glass-elevated p-3 rounded-lg text-xs pointer-events-none max-w-56"
      style={{ zIndex: 50 }}
    >
      <div className="font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{node.name}</div>
      <div className="flex flex-col gap-0.5" style={{ color: 'var(--text-secondary)' }}>
        <span>Initiative: <strong style={{ color: INITIATIVE_COLORS[node.strategicInitiative] }}>{node.strategicInitiative}</strong></span>
        <span>Relatedness: <strong>{formatRelatedness(rel)}</strong></span>
        <span>Attractiveness: <strong>{node.attractivenessScore}</strong></span>
        <span>SAM: <strong>{node.sam != null ? `$${(node.sam / 1_000_000).toFixed(1)}M` : '—'}</strong></span>
        <span>Priority: <strong>{node.priority ?? '—'}</strong></span>
        <span>Ring: <strong>{node.relatednessRing}</strong></span>
      </div>
    </div>
  );
}
